import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { get } from 'lodash';
import { toast } from 'react-toastify';
import { fetchUserAttributes } from 'aws-amplify/auth';
import {
  Container, Row, Col, Card, CardBody,
} from 'reactstrap';

import { Button, ButtonLink } from 'components/Button';
import {
  enableEmailMfa, isValidCode, awsLoginRequest, resolveErrorMessage,
} from 'utils/aws';
import { completeMfaChallenge } from 'actions/auth';
import CodeInput from './components/CodeInput';
import './style.scss';

const MAX_ADDITIONAL_ATTEMPTS = 3;
const RESEND_COOLDOWN_SECONDS = 60;
const VerifyByEmail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const email = get(location, 'state.email', 'your email');
  const isChallenge = get(location, 'state.mode') === 'challenge';
  const username = get(location, 'state.username', '');
  const password = get(location, 'state.password', '');
  const [code, setCode] = useState('');
  const [hasError, setHasError] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const isLocked = failedAttempts > MAX_ADDITIONAL_ATTEMPTS;
  const [resendCountdown, setResendCountdown] = useState(RESEND_COOLDOWN_SECONDS);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (resendCountdown <= 0) return undefined;
    const timer = setTimeout(() => setResendCountdown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  const handleResend = async () => {
    if (resendCountdown > 0 || isResending) {
      return;
    }

    if (!username || !password) {
      toast.info('Please sign in again to receive a new verification code.');
      navigate('/');
      return;
    }

    setIsResending(true);
    try {
      const outcome = await awsLoginRequest({ payload: { username, password } });
      if (outcome && outcome.challenge && outcome.challenge.type === 'EMAIL') {
        toast.success('A new verification code has been sent to your email.');
        setResendCountdown(RESEND_COOLDOWN_SECONDS);
      } else {
        toast.info('Please sign in again to receive a new verification code.');
        navigate('/');
      }
    } catch (err) {
      toast.error(err?.message || 'Could not resend the code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleVerify = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (!isValidCode(code)) {
      setHasError(true);
      return;
    }

    setIsLoading(true);
    setHasError(false);

    dispatch(completeMfaChallenge({
      code,
      trustDevice,
      success: () => {
        setIsLoading(false);
        navigate('/consumer/dashboard');
      },
      fail: (err) => {
        setIsLoading(false);
        setHasError(true);

        const rawMessage = resolveErrorMessage(err, 'That code did not match. Please try again.');
        const isInvalidCode = /invalid code or auth state/i.test(rawMessage);
        toast.error(
          isInvalidCode
            ? 'Invalid code. Please try again or request a new one with the link below the “Verify” button.'
            : rawMessage
        );
      },
      navigate,
    }));
  };

  const handleEnable = async () => {
    if (isLocked) {
      return;
    }

    if (!sessionStorage.getItem('idToken')) {
      toast.info('Please sign in first, then enable email verification from your account.');
      navigate('/');
      return;
    }

    setIsLoading(true);

    try {
      const attributes = await fetchUserAttributes();
      if (attributes?.email_verified !== 'true' && attributes?.email_verified !== true) {
        setIsLoading(false);
        toast.error(
          'Your email address must be verified before it can be used for sign-in verification.'
        );
        return;
      }
    } catch (err) {
      setIsLoading(false);
      toast.error(
        err?.message || 'Could not confirm your email is verified. Please try again.'
      );
      return;
    }

    try {
      await enableEmailMfa();
      setIsLoading(false);
      sessionStorage.setItem('mfaSatisfied', 'true');
      toast.success('Email verification has been enabled.');
      navigate('/consumer/dashboard');
    } catch (err) {
      setIsLoading(false);
      const nextFailedAttempts = failedAttempts + 1;
      setFailedAttempts(nextFailedAttempts);

      if (nextFailedAttempts > MAX_ADDITIONAL_ATTEMPTS) {
        toast.error(
          'Too many failed attempts to enable email verification. Please try again later '
          + 'or choose a different method.'
        );
      } else {
        toast.error(
          err?.message || 'Could not enable email verification. Please try again.'
        );
      }
    }
  };

  return (
    <div className="two-factor-page">
      <Container fluid>
        <Row>
          <Col>
            <Card className="tf-card">
              <CardBody className="p-4">
                <h3 className="tf-title">Verify by email</h3>
                {isChallenge ? (
                  <p className="tf-subtitle">
                    A 6 digit code has been sent to your email
                    {' '}
                    <strong>{email}</strong>
                    . Enter that code to complete sign-in.
                  </p>
                ) : (
                  <p className="tf-subtitle">
                    Each time you sign in, we&apos;ll send a 6-digit code to
                    {' '}
                    <strong>{email}</strong>
                    . Enter that code to complete sign-in.
                  </p>
                )}

                {isChallenge ? (
                  <form onSubmit={handleVerify}>
                    <CodeInput
                      value={code}
                      onChange={(val) => { setCode(val); setHasError(false); }}
                      hasError={hasError}
                      disabled={isLoading}
                      autoFocus
                    />

                    <div className="tf-checkbox">
                      <label className="tf-checkbox__row" htmlFor="trust-device">
                        <input
                          id="trust-device"
                          type="checkbox"
                          checked={trustDevice}
                          onChange={(e) => setTrustDevice(e.target.checked)}
                          disabled={isLoading}
                        />
                        Trust this device for 30 days
                      </label>
                    </div>

                    <Button
                      type="submit"
                      color="primary"
                      className="w-100 mt-3"
                      isLoading={isLoading}
                      onClick={handleVerify}
                    >
                      Verify
                    </Button>

                    <div className="text-center mt-4">
                      <p className="tf-code-label mb-1">Did not receive the email?</p>
                      {resendCountdown > 0 ? (
                        <span className="tf-subtitle">
                          Resend available in
                          {' '}
                          {resendCountdown}
                          s
                        </span>
                      ) : (
                        <ButtonLink
                          className="tf-link"
                          isDisabled={isResending}
                          onClick={handleResend}
                        >
                          Click here to resend the verification email
                        </ButtonLink>
                      )}
                    </div>
                  </form>
                ) : (
                  <>
                    <div className="tf-note mb-4">
                      <strong>Note:</strong>
                      {' '}
                      If you choose this method for sign-in verification, password resets cannot be
                      sent to same email address. You will not be able to reset password online in
                      the future.
                    </div>

                    <Button
                      type="button"
                      color="primary"
                      className="w-100"
                      isLoading={isLoading}
                      isDisabled={isLocked}
                      onClick={handleEnable}
                    >
                      Enable Email Verification
                    </Button>

                    <div className="text-center mt-3">
                      <ButtonLink className="tf-link" onClick={() => navigate('/two-factor/setup')}>
                        &larr; Back to setup options
                      </ButtonLink>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default VerifyByEmail;
